import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { connect } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import EventSelectorButton from '../../components/EventSelector/Button';
import EventSelector from '../../components/EventSelector/Modal';
import { usePage } from '../../helpers/hooks';
import { loadArrangeLayoutMap } from '../Mapping/Arrange/logic';
import DataExplore from './charts';
import compute, {
  annotateDetail,
  defaultOptionForSessions,
  findScanOption,
  nextScanOption,
  tileCoverageDetail,
} from './compute';
import {
  getCrowdCountSessions,
  loadDetectionsForOption,
  loadScanDocs,
  loadSrsOverlay,
} from './load';
import Controls from './preview/Controls';
import Preview from './preview';
import DetailDrawer from './sidebar/DetailDrawer';
import { EMPTY } from './srs/geometry';
import { requestPersistentStorage, unregisterLegacyAssetSw } from './signedAssetCache';
import { now as perfNow, span as perfSpan } from './perf';
import style from './style.module.scss';

const emptyBundle = {
  scans: [],
  tileBoxes: {},
  rekognitionLabels: [],
  overlay: EMPTY,
};

/** Static: render computed page object only. */
const PageContent = ({ data, chartData, actions }) => {
  const {
    loading, error, controls, preview, ready,
  } = data;

  let status = null;
  if ((loading.sessions || loading.scan) && !preview.showRegion && !preview.showStadium) {
    status = <div className={style.empty}><Spinner animation="border" /></div>;
  } else if (!loading.sessions && !controls.hasScans) {
    status = <div className={style.empty}>No crowd count scans for this event.</div>;
  } else if (!loading.scan && error.scan) {
    status = (
      <div className={style.empty}>
        Couldn&apos;t load this scan. Try another or refresh.
      </div>
    );
  } else if (!loading.scan && !error.scan && !preview.isGroup
    && preview.singleScan && !preview.tiles.length) {
    status = <div className={style.empty}>This scan has no tile images.</div>;
  }

  return (
    <>
      <Controls data={data} actions={actions} />
      {status}
      <Preview data={data} onOpenDetail={actions.onOpenDetail} />
      {ready ? (
        <DataExplore
          data={chartData}
          onOpenDetail={actions.onOpenDetail}
          onOpenTileCoverage={actions.onOpenTileCoverage}
        />
      ) : null}
    </>
  );
};

/**
 * Page shell: progressive load (scans → mosaic → detections → optional SRS),
 * then compute(raw). Charts / preview only render the object.
 */
const CrowdCount = ({ event, selectedEventsPage }) => {
  const page = usePage();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scanParam = searchParams.get('scan');
  const scanParamRef = useRef(scanParam);
  scanParamRef.current = scanParam;

  const [sessions, setSessions] = useState([]);
  const [option, setOption] = useState(null);
  const [bundle, setBundle] = useState(emptyBundle);
  const [layoutMap, setLayoutMap] = useState({});
  const [layoutsReady, setLayoutsReady] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [detectionsLoading, setDetectionsLoading] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [showBoxes, setShowBoxes] = useState(false);
  const [srsShow, setSrsShow] = useState(false);
  const [srsLoadedFor, setSrsLoadedFor] = useState('');
  const [detail, setDetail] = useState(null);

  const reloadLayouts = () => {
    if (!event) {
      setLayoutMap({});
      setLayoutsReady(true);
      return;
    }
    setLayoutsReady(false);
    loadArrangeLayoutMap()
      .then(setLayoutMap)
      .catch(() => setLayoutMap({}))
      .finally(() => setLayoutsReady(true));
  };

  useEffect(() => {
    reloadLayouts();
  }, [event]); // reloadLayouts is stable enough for event changes only
  useEffect(() => {
    unregisterLegacyAssetSw();
    requestPersistentStorage();
  }, []);

  useEffect(() => {
    if (event && (!eventId || event._id !== eventId)) {
      const q = searchParams.toString();
      navigate(
        { pathname: `/crowd-count/${event._id}`, search: q ? `?${q}` : '' },
        { replace: true },
      );
    }
  }, [event, eventId, navigate, searchParams]);

  useEffect(() => {
    if (!event || selectedEventsPage !== page || event._id !== eventId) return undefined;
    let cancelled = false;
    setSessionsLoading(true);
    setSessions([]);
    setOption(null);
    setBundle(emptyBundle);
    setSelectedLabels([]);
    setDetail(null);
    setSrsShow(false);
    setSrsLoadedFor('');

    getCrowdCountSessions(event.slugDate)
      .then((payload) => {
        if (cancelled) return;
        const list = payload?.sessions || [];
        setSessions(list);
        setOption(
          findScanOption(list, scanParamRef.current) || defaultOptionForSessions(list),
        );
      })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setSessionsLoading(false); });

    return () => { cancelled = true; };
  }, [event, eventId, selectedEventsPage, page]);

  // Apply ?scan=… (label or value; spaces OK) once sessions are loaded.
  useEffect(() => {
    if (!sessions.length || !scanParam) return;
    const hit = findScanOption(sessions, scanParam);
    if (hit && hit.value !== option?.value) setOption(hit);
  }, [sessions, scanParam, option?.value]);

  // Progressive: scans first → mosaic paints; then detections + overlay in parallel.
  // Overlay feeds Section Demographics / stats; srsShow only controls seat paint.
  useEffect(() => {
    let cancelled = false;
    const tLoad = perfNow();
    setScanLoading(true);
    setScanError(false);
    setDetectionsLoading(true);
    setBundle(emptyBundle);
    setSelectedLabels([]);
    setDetail(null);
    setSrsShow(false);
    setSrsLoadedFor('');

    loadScanDocs(option)
      .then(async ({ scans, error }) => {
        if (cancelled) return;
        perfSpan('load.scans', tLoad, { scans: scans.length, error });
        setBundle({
          scans,
          tileBoxes: {},
          rekognitionLabels: [],
          overlay: EMPTY,
        });
        setScanError(error);
        setScanLoading(false);

        if (error || !scans.length) {
          setDetectionsLoading(false);
          return;
        }

        // Let the browser paint mosaics before heavy JSON/mapping work.
        await new Promise((r) => { requestAnimationFrame(() => r()); });
        if (cancelled) return;

        const isGroup = option?.kind === 'session' && (option?.regionIds || []).length >= 2;
        const tDet = perfNow();

        const detectionsPromise = loadDetectionsForOption(option, scans).then((d) => {
          perfSpan('load.detections', tDet, {
            tiles: Object.keys(d.tileBoxes || {}).length,
            boxes: Object.values(d.tileBoxes || {}).reduce((n, a) => n + (a?.length || 0), 0),
          });
          return d;
        });
        const overlayPromise = loadSrsOverlay(scans)
          .then((overlay) => {
            perfSpan('load.overlay', tDet, { seats: overlay?.seats?.length || 0 });
            return overlay;
          })
          .catch(() => EMPTY);

        // Stadium: start overlay first (smaller); detections can finish later.
        // Single region: both in parallel.
        if (isGroup) {
          const overlay = await overlayPromise;
          if (cancelled) return;
          setBundle((prev) => ({ ...prev, overlay }));
          setSrsLoadedFor(option?.value || '');
          const detections = await detectionsPromise;
          if (cancelled) return;
          setBundle((prev) => ({
            ...prev,
            tileBoxes: detections.tileBoxes,
            rekognitionLabels: detections.rekognitionLabels,
          }));
        } else {
          const [detections, overlay] = await Promise.all([detectionsPromise, overlayPromise]);
          if (cancelled) return;
          setBundle((prev) => ({
            ...prev,
            tileBoxes: detections.tileBoxes,
            rekognitionLabels: detections.rekognitionLabels,
            overlay,
          }));
          setSrsLoadedFor(option?.value || '');
        }
        if (!cancelled) {
          setDetectionsLoading(false);
          perfSpan('load.total', tLoad);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setScanLoading(false);
        setDetectionsLoading(false);
        setScanError(true);
      });

    return () => { cancelled = true; };
  }, [option]);

  // If user toggles SRS before overlay finished, nothing extra — paint uses bundle.overlay.
  // If overlay failed earlier, retry on toggle.
  useEffect(() => {
    if (!srsShow || !bundle.scans.length) return undefined;
    const key = option?.value || '';
    if (srsLoadedFor === key) return undefined;

    let cancelled = false;
    loadSrsOverlay(bundle.scans)
      .then((overlay) => {
        if (cancelled) return;
        setBundle((prev) => ({ ...prev, overlay }));
        setSrsLoadedFor(key);
      })
      .catch(() => {
        if (cancelled) return;
        setBundle((prev) => ({ ...prev, overlay: EMPTY }));
        setSrsLoadedFor(key);
      });

    return () => { cancelled = true; };
  }, [srsShow, bundle.scans, option?.value, srsLoadedFor]);

  const includeStats = !detectionsLoading && bundle.scans.length > 0;

  // Heavy derive — NEVER depends on selectedLabels / showBoxes / srsShow.
  const computed = useMemo(() => compute({
    sessions,
    scans: bundle.scans,
    option,
    tileBoxes: bundle.tileBoxes,
    rekognitionLabels: bundle.rekognitionLabels,
    overlay: bundle.overlay,
    layoutMap,
    layoutsReady,
    sessionsLoading,
    scanLoading,
    scanError,
    detectionsLoading,
    includeStats,
  }), [
    sessions, bundle.scans, bundle.tileBoxes, bundle.rekognitionLabels, bundle.overlay,
    option, layoutMap, layoutsReady,
    sessionsLoading, scanLoading, scanError, detectionsLoading, includeStats,
  ]);

  // Selection is O(labels) — paint-only for mosaics; charts stay on `computed`.
  const selectedLabelSet = useMemo(() => new Set(selectedLabels), [selectedLabels]);

  const selectedOptions = useMemo(
    () => computed.controls.detectableOptions.filter((o) => selectedLabelSet.has(o.value)),
    [computed.controls.detectableOptions, selectedLabelSet],
  );

  // Keep preview identity stable across box/SRS toggles (CSS handles those).
  const preview = useMemo(() => ({
    ...computed.preview,
    selectedLabelSet,
  }), [computed.preview, selectedLabelSet]);

  const data = useMemo(() => {
    const next = {
      ...computed,
      ui: { showBoxes, srsShow },
      controls: { ...computed.controls, selectedOptions },
      preview,
    };
    return next;
  }, [computed, selectedOptions, preview, showBoxes, srsShow, selectedLabels.length]);

  // Charts ignore selection / box / SRS toggles entirely.
  const chartData = computed;

  const onOpenDetail = (payload) => {
    setDetail(annotateDetail(payload, bundle.overlay));
  };

  // Keep data-* attrs in sync immediately so mosaic hit-testing (reads the DOM
  // attr) and CSS visibility don't wait on the next React commit.
  const syncAttr = (name, on) => {
    document.querySelectorAll(`[${name}]`).forEach((el) => {
      el.setAttribute(name, on ? '1' : '0');
    });
  };

  const selectOption = (next) => {
    setOption(next);
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      if (next?.label) nextParams.set('scan', next.label);
      else nextParams.delete('scan');
      return nextParams;
    }, { replace: true });
  };

  const actions = {
    setOption: selectOption,
    setSelectedLabels: (next) => {
      setSelectedLabels(next);
      // Auto-show boxes when user picks labels (instant CSS); still paint layer async.
      if ((next || []).length && !showBoxes) {
        setShowBoxes(true);
        syncAttr('data-boxes-show', true);
      }
    },
    toggleShowBoxes: () => {
      setShowBoxes((v) => {
        const next = !v;
        syncAttr('data-boxes-show', next);
        return next;
      });
    },
    toggleSrsShow: () => {
      setSrsShow((v) => {
        const next = !v;
        syncAttr('data-srs-show', next);
        return next;
      });
    },
    goScan: (delta) => {
      const next = nextScanOption(data.controls.scanOptions, data.controls.optionValue, delta);
      if (next) selectOption(next);
    },
    onOpenDetail,
    onOpenTileCoverage: (row, col, kind) => {
      onOpenDetail(tileCoverageDetail(data.preview, row, col, kind));
    },
    closeDetail: () => setDetail(null),
    reloadLayouts,
    exportAnalysis: () => {
      const json = JSON.stringify(
        { ...computed, layoutMap },
        (_, v) => (v instanceof Set ? [...v] : v),
      );
      const href = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = href;
      a.download = `crowd-count-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(href);
    },
  };

  return (
    <div className="d-flex flex-column flex-grow-1">
      <div className="pageHeader">
        <h4>Crowd Count</h4>
        <EventSelectorButton showFutureEvents />
      </div>

      <div className={style.content}>
        {!event && (
          <div className={style.empty}>Select an event to review crowd count scans.</div>
        )}
        {event ? <PageContent data={data} chartData={chartData} actions={actions} /> : null}
      </div>

      <DetailDrawer detail={detail} onClose={actions.closeDetail} />
      <EventSelector singleSelection showTemplate />
    </div>
  );
};

export default connect(
  ({ events: { selectedEvents, selectedEventsPage } }) => ({
    event: selectedEvents[0],
    selectedEventsPage,
  }),
)(CrowdCount);
